// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable no-template-curly-in-string */
/* eslint-disable spire/fenced-imports */
import translate from "@insite/client-framework/Translate";
import printValue from "yup/es/util/printValue";

declare type Message<Extra extends Record<string, unknown> = {}> =
    | string
    | ((params: Extra & any) => unknown)
    | Record<PropertyKey, unknown>;

export interface MixedLocale {
    default?: Message;
    required?: Message;
    oneOf?: Message<{ values: any }>;
    notOneOf?: Message<{ values: any }>;
    notType?: Message;
    defined?: Message;
}

export interface StringLocale {
    length?: Message<{ length: number }>;
    min?: Message<{ min: number }>;
    max?: Message<{ max: number }>;
    matches?: Message<{ regex: RegExp }>;
    email?: Message<{ regex: RegExp }>;
    url?: Message<{ regex: RegExp }>;
    uuid?: Message<{ regex: RegExp }>;
    trim?: Message;
    lowercase?: Message;
    uppercase?: Message;
}

export interface NumberLocale {
    min?: Message<{ min: number }>;
    max?: Message<{ max: number }>;
    lessThan?: Message<{ less: number }>;
    moreThan?: Message<{ more: number }>;
    positive?: Message<{ more: number }>;
    negative?: Message<{ less: number }>;
    integer?: Message;
}

export interface DateLocale {
    min?: Message<{ min: Date | string }>;
    max?: Message<{ max: Date | string }>;
}

export interface ObjectLocale {
    noUnknown?: Message;
}

export interface ArrayLocale {
    length?: Message<{ length: number }>;
    min?: Message<{ min: number }>;
    max?: Message<{ max: number }>;
}

export interface BooleanLocale {
    isValue?: Message;
}

export interface LocaleObject {
    mixed?: MixedLocale;
    string?: StringLocale;
    number?: NumberLocale;
    date?: DateLocale;
    boolean?: BooleanLocale;
    object?: ObjectLocale;
    array?: ArrayLocale;
}

export const mixed: Required<MixedLocale> = {
    default: ({ path }) => translate("{0} is invalid", path),
    required: ({ path }) => translate("{0} is a required field", path),
    oneOf: ({ path, values }) => translate("{0} must be one of the following values: {1}", path, values),
    notOneOf: ({ path, values }) => translate("{0} must not be one of the following values: {1}", path, values),
    notType: ({ path, type, value, originalValue }) => translate("{0} must be a {1} type", path, type),
    defined: ({ path }) => translate("{0} must be defined", path),
};

export const string: Required<StringLocale> = {
    length: ({ path, length }) => translate("{0} must be exactly {1} characters", path, length),
    min: ({ path, min }) => translate("{0} must be at least {1} characters", path, min),
    max: ({ path, max }) => translate("{0} must be at most {1} characters", path, max),
    matches: ({ path, regex }) => translate("${0} must match the following: {1}", path, regex),
    email: ({ path }) => translate("{0} must be a valid email", path),
    url: ({ path }) => translate("{0} must be a valid URL", path),
    uuid: ({ path }) => translate("{0} must be a valid UUID", path),
    trim: ({ path }) => translate("{0} must be a trimmed string", path),
    lowercase: ({ path }) => translate("{0} must be a lowercase string", path),
    uppercase: ({ path }) => translate("{0} must be a upper case string", path),
};

export const number: Required<NumberLocale> = {
    min: ({ path, min }) => translate("{0} must be greater than or equal to {1}", path, min),
    max: ({ path, max }) => translate("{0} must be less than or equal to {1}", path, max),
    lessThan: ({ path, less }) => translate("{0} must be less than {1}", path, less),
    moreThan: ({ path, more }) => translate("{0} must be greater than {1}", path, more),
    positive: ({ path }) => translate("{0} must be a positive number", path),
    negative: ({ path }) => translate("{0} must be a negative number", path),
    integer: ({ path }) => translate("{0} must be an integer", path),
};

export const date: Required<DateLocale> = {
    min: ({ path, min }) => translate("{0} field must be later than {1}", path, min),
    max: ({ path, max }) => translate("${0} field must be at earlier than {1}", path, max),
};

export const boolean: BooleanLocale = {
    isValue: ({ path, value }) => translate("{0} field must be {1}", path, value),
};

export const object: Required<ObjectLocale> = {
    noUnknown: ({ path, unknown }) => translate("{0} field has unspecified keys: {1}", path, unknown),
};

export const array: Required<ArrayLocale> = {
    min: ({ path, min }) => translate("{0} field must have at least {1} items", path, min),
    max: ({ path, max }) => translate("{0} field must have less than or equal to {1} items", path, max),
    length: ({ path, length }) => translate("{0} must have {1} items", path, length),
};

export default Object.assign(Object.create(null), {
    mixed,
    string,
    number,
    date,
    object,
    array,
    boolean,
});
